import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    setOpenSubmenu(null);
    
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      return;
    }

    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    }
  }, [location]);

  const navItems = [
    { href: '/#sobre', label: 'Sobre', id: 'sobre' },
    { 
      label: 'Áreas de Atuação', 
      id: 'how-we-help',
      href: '/#how-we-help',
      subItems: [
        { href: '/#areas-de-atuacao', label: 'Trabalhador', id: 'areas-de-atuacao' },
        { href: '/#areas-de-atuacao', label: 'Empresas', id: 'areas-de-atuacao' },
        { href: '/#areas-de-atuacao', label: 'Condominial', id: 'areas-de-atuacao' }
      ]
    },
    { href: '/#metodologia', label: 'Como funciona?', id: 'metodologia' },
    { href: '/#faq', label: 'Dúvidas Frequentes', id: 'faq' },
    { href: '/blog', label: 'Blog' },
    { href: '/contato', label: 'Contato' }
  ];

  const toggleSubmenu = (label: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-brand-sand/95 backdrop-blur-md border-b border-brand-slate/20 z-50 transition-all duration-300" style={{ height: '80px' }}>
      <div className="wrapper">
        <nav className="flex items-center justify-between py-4">
          <div className="flex-1">
            <a 
              href="/" 
              className="flex flex-col items-start hover:opacity-80 transition-opacity duration-200 ml-4 md:ml-6"
            >
              <img 
                src="/images/luara-rezende-horizontal.svg" 
                alt="Luara Rezende Advocacia" 
                className="h-10 w-auto"
              />
              <span className="text-[10px] text-brand-slate/70 font-medium tracking-wider mt-1 uppercase ml-1">
                Direito do Trabalho & Condominial
              </span>
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                <a
                  href={item.href}
                  className="text-brand-ink hover:text-brand-accent font-medium transition-colors duration-200 flex items-center gap-1 py-4"
                  onClick={(e) => {
                    if (item.id && !item.subItems) {
                      e.preventDefault();
                      scrollToSection(item.id);
                    }
                  }}
                >
                  {item.label}
                  {item.subItems && <ChevronDown size={16} className="group-hover:rotate-180 transition-transform duration-200" />}
                </a>

                {/* Dropdown Desktop */}
                {item.subItems && (
                  <div className="absolute top-full left-0 w-56 bg-white shadow-xl border border-brand-slate/10 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 overflow-hidden">
                    <div className="py-2">
                      {item.subItems.map(sub => (
                        <a
                          key={sub.label}
                          href={sub.href}
                          className="block px-4 py-3 text-sm text-brand-ink hover:text-brand-accent hover:bg-brand-sand/50 transition-colors"
                          onClick={(e) => {
                            if (sub.id) {
                              e.preventDefault();
                              scrollToSection(sub.id);
                            }
                          }}
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-brand-ink hover:text-brand-accent transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden border-t border-brand-slate/20 mobile-menu bg-brand-sand absolute left-0 right-0 shadow-lg"
            >
              <div className="py-4 px-6 space-y-2 max-h-[calc(100vh-80px)] overflow-y-auto">
                {navItems.map((item) => (
                  <div key={item.label} className="border-b border-brand-slate/10 last:border-0 pb-2 last:pb-0">
                    <div className="flex items-center justify-between">
                      <a
                        href={item.href}
                        className="mobile-nav-link flex-1 py-2"
                        onClick={(e) => {
                          if (item.id && !item.subItems) {
                            e.preventDefault();
                            scrollToSection(item.id);
                          } else if (item.subItems) {
                            toggleSubmenu(item.label, e);
                          }
                        }}
                      >
                        {item.label}
                      </a>
                      {item.subItems && (
                        <button 
                          onClick={(e) => toggleSubmenu(item.label, e)}
                          className="p-2 text-brand-slate"
                        >
                          <ChevronDown size={20} className={`transition-transform duration-200 ${openSubmenu === item.label ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    
                    {/* Accordion Mobile */}
                    <AnimatePresence>
                      {item.subItems && openSubmenu === item.label && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-4 border-l-2 border-brand-accent/20 ml-2 mt-2 space-y-2 overflow-hidden"
                        >
                          {item.subItems.map(sub => (
                            <a
                              key={sub.label}
                              href={sub.href}
                              className="block py-2 text-sm text-brand-ink hover:text-brand-accent transition-colors"
                              onClick={(e) => {
                                if (sub.id) {
                                  e.preventDefault();
                                  scrollToSection(sub.id);
                                }
                              }}
                            >
                              {sub.label}
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
